import { Button, FormControl, FormLabel, Input, Radio, RadioGroup, SimpleGrid, Stack, useToast } from "@chakra-ui/react"
import { Center } from "@chakra-ui/layout"
import { Field, FieldProps, Form, Formik } from "formik"
import React, { useCallback, useEffect } from "react"
import CarrotClient from "../../lib/carrot-api"
import Settings from "../../utils/CarrotConfig"
import changeHealthcare401Redirect from "./ChangeHealthcare401Redirect"
const client = new CarrotClient(Settings.CARROT_BACKEND_URL)

export default function SearchInputs({
  setData,
  initialEmployeeId
}: {
  setData: (data: any) => void
  initialEmployeeId?: string
}): JSX.Element {
  const toast = useToast()

  async function getUnfilteredLogs() {
    const data = await client.getChangeHealthcareLogs()
    setData(data)
  }

  const failedRequestToast = useCallback(() => {
    toast({
      title: "Request Failed",
      description: "Check your input and try again",
      status: "error",
      position: "top-right",
      duration: 5000,
      isClosable: true
    })
  }, [toast])

  async function getLogsForEmployeeId(employeeId, startDate, endDate) {
    try {
      const data = await client.getChangeHealthcareLogsForEmployeeId(employeeId, startDate, endDate)
      setData(data)
    } catch (e) {
      changeHealthcare401Redirect(e)
      failedRequestToast()
    }
  }

  async function getLogsForCompanyId(companyId) {
    try {
      const data = await client.getChangeHealthcareLogsForCompanyId(companyId)
      setData(data)
    } catch (e) {
      changeHealthcare401Redirect(e)
      failedRequestToast()
    }
  }

  async function getLogsForTradingPartnerId(tradingPartnerId) {
    try {
      const data = await client.getChangeHealthcareLogsForTradingPartnerId(tradingPartnerId)
      setData(data)
    } catch (e) {
      changeHealthcare401Redirect(e)
      failedRequestToast()
    }
  }

  useEffect(() => {
    if (initialEmployeeId) {
      client
        .getChangeHealthcareLogsForEmployeeId(initialEmployeeId, undefined, undefined)
        .then(setData)
        .catch((e) => {
          changeHealthcare401Redirect(e)
          failedRequestToast()
        })
    }
  }, [initialEmployeeId, setData, failedRequestToast])

  return (
    <>
      <SimpleGrid columns={2} spacing={10}>
        <Center>
          <Formik
            initialValues={{}}
            onSubmit={(values, { setSubmitting }) => {
              getUnfilteredLogs()
              setSubmitting(false)
            }}
          >
            {({ isSubmitting }) => (
              <Form>
                <Button mt={4} colorScheme="carrotOrange" isLoading={isSubmitting} type="submit">
                  Load Latest 500 Records
                </Button>
              </Form>
            )}
          </Formik>
        </Center>
        <Formik
          initialValues={{
            selectedFilter: "employeeId",
            searchValue: initialEmployeeId ?? "",
            startDate: undefined,
            endDate: undefined
          }}
          onSubmit={(values, { setSubmitting }) => {
            switch (values.selectedFilter) {
              case "employeeId":
                getLogsForEmployeeId(values.searchValue, values.startDate, values.endDate)
                break
              case "companyId":
                getLogsForCompanyId(values.searchValue)
                break
              case "tradingPartnerId":
                getLogsForTradingPartnerId(values.searchValue)
                break
              default:
                getUnfilteredLogs()
            }
            setSubmitting(false)
          }}
        >
          {({ isSubmitting, values }) => (
            <Form>
              <Field name="selectedFilter">
                {({ field }: FieldProps) => {
                  const { onChange, ...rest } = field
                  return (
                    <FormControl id="selectedFilter">
                      <FormLabel htmlFor={"selectedFilter"} hidden={true}>
                        Search by
                      </FormLabel>
                      <RadioGroup {...rest} id={"selectedFilter"} mt={4}>
                        <Stack direction="row">
                          <Radio onChange={onChange} value="employeeId">
                            Employee ID
                          </Radio>
                          <Radio onChange={onChange} value="companyId">
                            Company ID
                          </Radio>
                          <Radio onChange={onChange} value="tradingPartnerId">
                            Trading Partner ID
                          </Radio>
                        </Stack>
                      </RadioGroup>
                    </FormControl>
                  )
                }}
              </Field>
              <Field name="searchValue">
                {({ field }) => (
                  <FormControl>
                    <FormLabel htmlFor="searchValue" hidden={true}>
                      Search value
                    </FormLabel>
                    <Input {...field} id="searchValue" placeholder="Enter ID to search" mt={4} />
                  </FormControl>
                )}
              </Field>
              {values.selectedFilter === "employeeId" && (
                <>
                  <Field>
                    {({ field }) => (
                      <FormControl>
                        <FormLabel htmlFor="startDate" mt={4}>
                          Start Date
                        </FormLabel>
                        <Input {...field} type="date" id="startDate" value={values.startDate} />
                      </FormControl>
                    )}
                  </Field>
                  <Field>
                    {({ field }) => (
                      <FormControl>
                        <FormLabel htmlFor="endDate" mt={4}>
                          End Date
                        </FormLabel>
                        <Input {...field} type="date" id="endDate" value={values.endDate} />
                      </FormControl>
                    )}
                  </Field>
                </>
              )}
              <Button mt={4} colorScheme="carrotOrange" isLoading={isSubmitting} type="submit">
                Load Latest 500 Records for ID
              </Button>
            </Form>
          )}
        </Formik>
      </SimpleGrid>
    </>
  )
}
